import { Link, useNavigate } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { CLP, formatSessionRange } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { User, CalendarClock, ListOrdered, Wallet, CreditCard, Trash2, ExternalLink } from "lucide-react";

const STATE_BADGE = {
  disponible: { t: "Disponible", c: "bg-[hsl(145,42%,38%)] text-white hover:bg-[hsl(145,42%,38%)]" },
  agotado: { t: "Sin cupos", c: "bg-destructive text-destructive-foreground" },
  iniciado: { t: "Taller iniciado", c: "bg-amber-500 text-white hover:bg-amber-500" },
  terminado: { t: "Terminado", c: "bg-white/20 hover:bg-white/20" },
  cancelado: { t: "Cancelado", c: "bg-destructive text-destructive-foreground" },
};
const DISABLED_LABEL = { agotado: "Sin cupos", iniciado: "Taller iniciado", terminado: "Taller terminado", cancelado: "Cancelado" };

const Field = ({ icon: Icon, label, children, testId }) => (
  <div className="flex items-start gap-2.5">
    <Icon className="w-4 h-4 mt-0.5 text-primary shrink-0" />
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="text-sm font-medium break-words" data-testid={testId}>{children}</div>
    </div>
  </div>
);

export function WishSlide({ it, onPay, onRemove }) {
  const w = it.workshop;
  const badge = STATE_BADGE[it.state] || STATE_BADGE.disponible;
  const img = fileUrl(w.images?.[0]);
  const navigate = useNavigate();
  const pay = () => {
    if (!w.needs_choice) { onPay(it); return; }
    toast.info("Elige tu grupo o tus sesiones para completar la reserva");
    navigate(`/taller/${it.workshop_id}`);
  };
  return (
    <div className="rounded-3xl overflow-hidden border bg-card shadow-[0_14px_40px_-16px_rgba(121,68,228,0.45)]" data-testid={`saved-item-${it.id}`}>
      <div className="bg-primary text-primary-foreground p-5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] uppercase tracking-[0.2em] opacity-80">VIVOTaller · Mi deseo</p>
          <Badge className={badge.c} data-testid={`saved-state-${it.id}`}>{badge.t}</Badge>
        </div>
        <h3 className="font-display font-extrabold text-xl mt-2 leading-tight break-words" data-testid={`saved-name-${it.id}`}>{w.name}</h3>
        <p className="text-sm opacity-90 mt-1 flex items-center gap-1.5"><User className="w-3.5 h-3.5" />Maestro: <strong>{w.business_name || "—"}</strong></p>
      </div>
      <div className="relative flex items-center">
        <div className="w-5 h-5 rounded-full bg-background -ml-2.5 border-r" />
        <div className="flex-1 border-t-2 border-dashed border-border" />
        <div className="w-5 h-5 rounded-full bg-background -mr-2.5 border-l" />
      </div>
      <div className="p-5 space-y-3.5">
        {img && <img src={img} alt="" className="w-full h-40 rounded-2xl object-cover" data-testid={`saved-img-${it.id}`} />}
        <Field icon={CalendarClock} label="Próxima sesión" testId={`saved-next-${it.id}`}>
          {it.next_session ? formatSessionRange(it.next_session, w.duration_min || 0) : "Sin próximas sesiones"}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field icon={ListOrdered} label="Sesiones por alumno" testId={`saved-sessions-${it.id}`}>{w.sessions_per_student || "—"}</Field>
          <Field icon={Wallet} label="Precio" testId={`saved-price-${it.id}`}>{CLP(w.price_clp)}</Field>
        </div>
        {it.can_pay ? (
          <Button className="w-full" onClick={pay} data-testid={`saved-pay-${it.id}`}>
            <CreditCard className="w-4 h-4 mr-1.5" />Reservar
          </Button>
        ) : (
          <Button className="w-full" variant="outline" disabled data-testid={`saved-pay-disabled-${it.id}`}>{DISABLED_LABEL[it.state] || "No disponible"}</Button>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Button asChild variant="outline" size="sm"><Link to={`/taller/${it.workshop_id}`} data-testid={`saved-view-${it.id}`}><ExternalLink className="w-4 h-4 mr-1" />Ver taller</Link></Button>
          <Button variant="outline" size="sm" className="text-destructive border-destructive/40 hover:bg-destructive/5" onClick={() => onRemove(it.id)} data-testid={`saved-remove-${it.id}`}>
            <Trash2 className="w-4 h-4 mr-1" />Eliminar
          </Button>
        </div>
      </div>
    </div>
  );
}
